import { Button, Grid, IconButton, Tooltip, Typography, styled } from "@mui/material";
import { idDocumentsApi } from "api";
import { DocumentFileEntity } from "api/generated";
import AppModal from "components/AppModal";
import FlexBox from "components/flexbox/FlexBox";
import FileUploadInput from "components/input-fields/FileUploadInput";
import DeleteIcon from "icons/DeleteIcon";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from '@mui/icons-material/Close';
import UploadingOverlay from "components/UploadOverlay";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { imagesToPDF } from '@coderosh/images-to-pdf'
import CustomToast from "components/CustomToast/customToast";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PDFMerger from "pdf-merger-js";
import { useEncryption } from "contexts/EncryptionKeyContext";
import { decryptFile, encryptFile } from "utils/encryptionDecryptionAgreements";
import { download, getExtensionFromMime } from "utils/utils";

interface ModalProps {
    open: boolean;
    documentId: number;
    documentName: string;
    onClose: () => void;
    data?: DocumentFileEntity[];
    isEncrypted?: boolean;
    setData: (files: DocumentFileEntity[]) => void;
    onDocumentRemove: (id: number, index: number) => void;
    setIsLoading?: (isLoading: boolean) => void;
    isLoading?: boolean;
    setShowToastCallback?: (param: {
        show: boolean;
        message: string;
        type: string;
    }) => void;
    edit?: boolean;
    isCompiledByEmployee?: boolean,
    userMode: boolean
}


const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 500,
    minWidth: 300,

    [theme.breakpoints.down(325)]: { maxWidth: '100%' },
}));

const DocumentFilesModal: FC<ModalProps> = ({ open, onClose, setData, data, isEncrypted, documentId, documentName, onDocumentRemove, edit, setIsLoading, isLoading, setShowToastCallback, isCompiledByEmployee, userMode }) => {
    const { t } = useTranslation();
    const [showedData, setShowedData] = useState(data);
    const [showToast, setShowToast] = useState<{ show: boolean, message: string, icon: any }>({ show: false, message: '', icon: undefined });
    const { encryptionKey } = useEncryption();

    useEffect(() => {
        if (data && data.length > 0) {
            setShowedData(data);
        }
        else
            onClose();
    }, [data]);

    const addDocumentFile = async (files: File[]) => {
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            if (encryptionKey && isEncrypted) {
                const encFile = await encryptFile(files[i], encryptionKey);
                formData.append('files', encFile)
            } else if (!encryptionKey && isEncrypted) {
                if (setShowToastCallback)
                    setShowToastCallback({ show: true, message: t('alerts.insertEncKey'), type: 'Error' });
            } else {
                formData.append('files', files[i])
            }
        }
        if (documentId) {
            if (setIsLoading)
                setIsLoading(true);

            const urlParams = new URLSearchParams(window.location.search);
            const token = urlParams.get('token');
            const tenant = urlParams.get('tenant');

            const newDocument = !isCompiledByEmployee ?
                (await idDocumentsApi.createFile(
                    String(documentId),
                    false,
                    false,
                    {
                        data: formData
                    })).data : tenant && token &&
                (await idDocumentsApi.createFileWithToken(
                    String(documentId),
                    tenant,
                    {
                        data: formData,
                        params: {
                            token
                        }
                    })).data;

            if (setIsLoading)
                setIsLoading(false);
            if (setShowToastCallback && newDocument) {
                setShowToastCallback({ show: true, message: t('alerts.fileEntred'), type: 'Created' });
                if (data)
                    setData(data.concat(newDocument));
                else
                    setData(newDocument);
            }
        }
    }

    const generatePDF = async () => {
        try {
            if (!showedData) return;
            if (isEncrypted && !encryptionKey) {
                if (setShowToastCallback) setShowToastCallback({ show: true, message: t('alerts.encryptedFile'), type: 'Error' });
            } else {
                const merger = new PDFMerger();
                for (const file of showedData) {
                    const response = await fetch(file.filePath);
                    const type = response.headers.get('content-type');
                    console.log(type);
                    const fileResponse = (new File([await (await fetch(file.filePath)).arrayBuffer()], documentName, { type: type || '' }));
                    const decryptedFile = isEncrypted ? await decryptFile(fileResponse, encryptionKey) : fileResponse;
                    if (type?.includes('pdf')) {
                        await merger.add(decryptedFile);
                    } else if (type?.includes('image')) {
                        const pdf = await imagesToPDF([await decryptedFile.arrayBuffer()]);
                        await merger.add(pdf.arrayBuffer());
                    } else {
                        setShowToast({ message: t('alerts.fileMergeWarning'), show: true, icon: <WarningAmberIcon /> })
                    }
                }
                await merger.save(documentName + '_joined');
            }
        }
        catch (e) {
            setShowToast({ message: t('alerts.fileMergeError'), show: true, icon: undefined })
            console.error(e);
        }

    }

    return (
        <StyledAppModal
            open={open}
            handleClose={onClose}
        >
            {showToast.show &&
                <CustomToast
                    duration={8000}
                    onClose={() => {
                        setShowToast({ show: false, message: '', icon: undefined })
                    }}
                    message={showToast.message}
                    icon={showToast.icon}
                    color={showToast.icon ? 'warning' : undefined}
                />
            }

            {isLoading &&
                <UploadingOverlay />
            }

            {showedData && showedData.length > 0 && (
                <Grid container columns={9} alignItems={'center'}>
                    <Grid item xs={3} textAlign={'center'}>
                        <Tooltip title={t('documents.downloadAsPDF')}>
                            <IconButton disabled={!data || data?.length === 0} onClick={generatePDF}><PictureAsPdfIcon /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={3}><Typography fontWeight={'bold'} textAlign={'center'}>{t('documents.fileModal.file')}</Typography></Grid>
                    <Grid item xs={3} textAlign={'center'}><IconButton onClick={onClose}><CloseIcon /></IconButton></Grid>
                </Grid>
            )}

            {showedData ?
                (<>
                    {showedData.map((file, index) => {
                        return (
                            <Grid container key={file.id} paddingY={1} justifyContent={'space-evenly'} borderBottom={1} borderColor={"lightgray"}>
                                <Grid container columns={9} alignItems={'center'} textAlign={'center'}>
                                    <Grid item xs={3}>
                                        {t('documents.fileModal.fileNumber', { number: index + 1 })}
                                    </Grid>
                                    <Grid item xs={3} textAlign={'center'}>
                                        <Button variant='white' onClick={async () => {
                                            const response = await fetch(file.filePath);
                                            const result = new File([await response.arrayBuffer()], documentName);
                                            const extension = getExtensionFromMime(response.headers.get('content-type') ?? '');
                                            if (isEncrypted && !encryptionKey) {
                                                if (setShowToastCallback) setShowToastCallback({ show: true, message: t('alerts.encryptedFile'), type: 'Error' });
                                            } else if (isEncrypted && encryptionKey) {
                                                download(await decryptFile(result, encryptionKey), documentName + (extension ?? ''));
                                            } else {
                                                download(result, documentName + (extension ?? ''));
                                            }
                                        }}>
                                            {t('employees.contract.contractFile')}
                                        </Button>
                                    </Grid>
                                    <Grid item xs={3} >
                                        <FlexBox justifyContent={'center'}>
                                            {edit ?? (
                                                !userMode &&
                                                <IconButton>
                                                    <DeleteIcon
                                                        sx={{ alignSelf: 'center', display: 'flex', justifySelf: 'center' }}
                                                        color="error"
                                                        onClick={() => {
                                                            onDocumentRemove(file.id, index);
                                                        }}
                                                        style={{
                                                            cursor: 'pointer',

                                                        }}
                                                    />
                                                </IconButton>
                                            )}
                                        </FlexBox>

                                    </Grid>
                                </Grid>

                            </Grid>
                        )
                    })}
                </>
                ) :
                (<></>)}
            <Grid xs={12} columns={9} container justifyItems={'center'} paddingTop={1}>
                <Grid item xs={3} />
                {edit ?? (
                    <>
                        <Grid item xs={3} textAlign={'center'}>
                            <FileUploadInput
                                resetOnChange
                                maxDimension={100}
                                handleOnChange={(e) => {
                                    if (e.target.files)
                                        addDocumentFile(Array.from(e.target.files));
                                }} />
                        </Grid>
                    </>
                )}
                <Grid item xs={3} />
            </Grid>
        </StyledAppModal>
    )
}

export default DocumentFilesModal;